import LoadableContext from './LoadableContext';
import { Context, createContext, PropsWithChildren, useContext } from 'react';

let loadedConfig: AppConfig | undefined

export interface AppConfig {
    apiEndpoint: string;
    // Cloud Run's own URL, bypassing the Firebase Hosting rewrite. Hosting buffers the entire
    // response and enforces a fixed 60s timeout on the rewrite regardless of Cloud Run's own
    // (longer) request timeout, which breaks long-running streamed responses -- migrationStreamApi.ts
    // uses this instead of apiEndpoint for exactly that reason. See deploy/fb-app.tf.
    directApiEndpoint: string;
    firebaseConfig: {
        apiKey: string;
        authDomain: string;
        projectId: string;
        storageBucket: string;
        messagingSenderId: string;
        appId: string;
    }
    // MSAL (public client) config for the webapp's own sign-in against Microsoft Entra ID
    // (contracts/app-config.json.md). Deliberately no `authorityMetadata`/`knownAuthorities` —
    // under FR-006 there is no non-Microsoft authority, so those fields would be dead
    // configuration that also weakens MSAL's own authority validation.
    msalConfig: {
        clientId: string;
        authority: string;
        scope: string;
    }
    deployment: {
        image: string;
        date: string;
        env: string;
    }
    tenant: string;
    usePopup: boolean;
    emulator?: {
        enabled: boolean;
        authPort: string;
        firestorePort: number;
    };
}

const appConfig = async (): Promise<AppConfig> => {
    if (!loadedConfig) {
        const response = await fetch("/app-config.json");
        if (!response.ok) throw new Error("Failed to load Firebase config");

        loadedConfig = await response.json();
        if (loadedConfig !== undefined) {
            loadedConfig.firebaseConfig.authDomain = window.location.hostname
        }
    }

    return loadedConfig!;
};

const ConfigContext: Context<AppConfig | undefined> = createContext<AppConfig | undefined>(undefined);

export const useConfigContext = () => {
    const context = useContext(ConfigContext);
    if (context === undefined) {
        throw new Error("useConfigContext must be used within AppConfigContext");
    }
    return context;
}

export default function AppConfigContext(props: PropsWithChildren) {
    return LoadableContext<AppConfig>({
        ...props,
        name: "AppConfig",
        context: ConfigContext,
        loader: appConfig
    })
}